import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatCostLong, formatDate } from "@/lib/format";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 25;

// Cross-org audit log of manual credit movements (admin topups +
// adjustments). Org names are mapped from the enriched orgs list the admin
// area already loads elsewhere.
export function AdminBillingGrantsPage() {
  useDocumentTitle("Admin: Billing grants");
  const [offset, setOffset] = useState(0);

  const grants = useQuery({
    queryKey: ["admin-billing-grants", offset],
    queryFn: () => api.admin.billingGrants(PAGE_SIZE, offset),
    placeholderData: keepPreviousData,
  });
  const orgs = useQuery({
    queryKey: ["admin-organizations"],
    queryFn: api.admin.organizations,
  });

  const orgName = (id: string) =>
    orgs.data?.data?.find((o) => o.id === id)?.name ?? id;

  if (grants.isLoading) return <LoadingIndicator />;
  if (grants.isError || !grants.data) {
    return (
      <div className="p-6">
        <InlineError error={grants.error ?? "Failed to load grants"} />
      </div>
    );
  }

  const data = grants.data;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Credit grants</h1>
        <p className="text-sm text-muted-foreground">
          Every manual credit movement across all organizations — admin top-ups
          and balance adjustments, newest first.
        </p>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Organization</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">By</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">Balance after</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {formatDate(row.createdAt)}
                </td>
                <td className="px-3 py-2">
                  <Link
                    to="/superadmin/organizations/$orgId"
                    params={{ orgId: row.organizationId }}
                    search={{ tab: "billing" }}
                    className="font-medium hover:underline"
                  >
                    {orgName(row.organizationId)}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <Badge variant="secondary">{row.type}</Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{row.description ?? "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{row.createdBy ?? "—"}</td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${
                    row.amount < 0 ? "text-destructive" : "text-green-600"
                  }`}
                >
                  {row.amount > 0 ? "+" : ""}
                  {formatCostLong(row.amount)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatCostLong(row.balanceAfter)}
                </td>
              </tr>
            ))}
            {data.entries.length === 0 && (
              <tr className="border-t">
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No manual credit movements yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="tabular-nums">
            {data.offset + 1}–{Math.min(data.offset + data.entries.length, data.total)} of{" "}
            {data.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={data.offset === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={data.offset + data.entries.length >= data.total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
